<template>
  <div class="sorted-packages-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">
          <i class="fal fa-check-circle"></i>
          ພັດສະດຸຄັດແຍກແລ້ວ
        </h2>
        <span class="machine-badge" v-if="machineCode">
          <i class="fal fa-cog"></i>
          {{ machineCode }}
        </span>
      </div>
      <div class="header-right">
        <div class="date-filter">
          <span class="filter-label">ວັນທີ:</span>
          <date-picker
            v-model="selectedDate"
            type="date"
            format="DD/MM/YYYY"
            value-type="YYYY-MM-DD"
            placeholder="ເລືອກວັນທີ"
            :clearable="true"
            :disabled-date="(date) => date > new Date()"
            @change="onDateFilterChange"
            class="date-picker-input"
          />
        </div>
        <div class="status-filter">
          <span class="filter-label">ສະຖານະ:</span>
          <model-list-select
            :list="statusOptions"
            v-model="statusFilter"
            placeholder="ເລືອກສະຖານະ..."
            option-text="label"
            option-value="value"
            @input="onStatusFilterChange"
            class="status-select"
          />
        </div>
        <div class="auto-refresh-control">
          <span class="refresh-label">ໂຫຼດອັດຕະໂນມັດ:</span>
          <div class="refresh-options">
            <button
              :class="['refresh-btn', { active: autoRefreshInterval === 0 }]"
              @click="setAutoRefresh(0)"
            >
              ປິດ
            </button>
            <button
              :class="['refresh-btn', { active: autoRefreshInterval === 6000 }]"
              @click="setAutoRefresh(6000)"
            >
              6s
            </button>
            <button
              :class="[
                'refresh-btn',
                { active: autoRefreshInterval === 10000 },
              ]"
              @click="setAutoRefresh(10000)"
            >
              10s
            </button>
          </div>
          <span v-if="autoRefreshInterval > 0" class="refresh-indicator">
            <i class="fal fa-sync-alt fa-spin"></i>
          </span>
        </div>
        <vs-button flat @click="fetchPackages" :loading="isLoading">
          <i class="fal fa-sync-alt" style="margin-right: 5px"></i>
          ໂຫຼດຄືນໃໝ່
        </vs-button>
      </div>
    </div>

    <!-- Stats Summary -->
    <div class="stats-summary-row">
      <div class="stats-summary">
        <div class="summary-item">
          <span class="summary-label">ທັງໝົດ:</span>
          <span class="summary-value" v-if="!isLoading">{{ totalCount.toLocaleString() }}</span>
          <span class="summary-value loading" v-else>...</span>
        </div>
        <div class="summary-item sorted">
          <span class="summary-label">ຄັດແຍກແລ້ວ:</span>
          <span class="summary-value" v-if="!isLoading">{{ sortedCount.toLocaleString() }}</span>
          <span class="summary-value loading" v-else>...</span>
        </div>
        <div class="summary-item packed">
          <span class="summary-label">ແພັກແລ້ວ:</span>
          <span class="summary-value" v-if="!isLoading">{{ packedCount.toLocaleString() }}</span>
          <span class="summary-value loading" v-else>...</span>
        </div>
      </div>
      <div class="search-bar">
        <div class="search-input-wrapper">
          <i class="fal fa-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            @keyup.enter="onSearch"
            placeholder="ຄົ້ນຫາເລກພັດສະດຸ, ບາໂຄດຖົງ..."
            class="search-input"
          />
          <i
            v-if="searchQuery"
            class="fal fa-times clear-icon"
            @click="clearSearch"
          ></i>
        </div>
        <vs-button @click="onSearch" :loading="isLoading">
          <i class="fal fa-search" style="margin-right: 5px"></i>
          ຄົ້ນຫາ
        </vs-button>
      </div>
    </div>

    <!-- Loading State -->
    <div class="loading-container" v-if="isLoading">
      <Loading />
    </div>

    <!-- Error State -->
    <div class="error-container" v-else-if="error">
      <div class="error-content">
        <i class="fal fa-exclamation-circle"></i>
        <p>{{ error }}</p>
        <vs-button @click="fetchPackages">ລອງໃໝ່</vs-button>
      </div>
    </div>

    <!-- No Machine Code -->
    <div class="error-container" v-else-if="!machineCode">
      <div class="error-content">
        <i class="fal fa-exclamation-triangle"></i>
        <p>ບໍ່ພົບລະຫັດເຄື່ອງຈັກ</p>
      </div>
    </div>

    <!-- Packages Table -->
    <div class="table-container" v-else>
      <table class="packages-table" v-if="packages.length > 0">
        <thead>
          <tr>
            <th class="col-no">ລຳດັບ</th>
            <th class="col-parcel">ເລກພັດສະດຸ</th>
            <th class="col-status">ສະຖານະ</th>
            <th class="col-container">ບາໂຄດຖົງ</th>
            <th class="col-chute">ຊ່ອງ</th>
            <th class="col-error">ຂໍ້ຜິດພາດ</th>
            <th class="col-date">ວັນທີສະແກນຄັດແຍກ</th>
            <th class="col-date">ວັນທີເຄື່ອນໄຫວລ່າສຸດ</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(pkg, index) in packages" :key="pkg.id">
            <td class="col-no">{{ index + 1 }}</td>
            <td class="col-parcel">
              <span class="parcel-actions">
                <span class="parcel-number">{{ pkg.parcel_bill_number }}</span>
                <i
                  class="fal fa-barcode action-icon"
                  title="ສະແກນບາໂຄດ"
                  @click="showBarcode(pkg.parcel_bill_number)"
                ></i>
                <i
                  :class="[
                    'fal',
                    copiedParcel === pkg.parcel_bill_number
                      ? 'fa-check'
                      : 'fa-copy',
                    'action-icon',
                    { copied: copiedParcel === pkg.parcel_bill_number },
                  ]"
                  title="ກ໋ອບປີ້"
                  @click="copyParcelNumber(pkg.parcel_bill_number)"
                ></i>
              </span>
            </td>
            <td class="col-status">
              <span :class="['status-badge', getStatusClass(pkg.status)]">
                {{ getStatusLabel(pkg.status) }}
              </span>
            </td>
            <td class="col-container">
              <span class="container-barcode" v-if="getContainerBarcode(pkg)">
                <span class="barcode-text">{{ getContainerBarcode(pkg) }}</span>
                <i
                  :class="[
                    'fal',
                    copiedContainer === getContainerBarcode(pkg)
                      ? 'fa-check'
                      : 'fa-copy',
                    'action-icon',
                    { copied: copiedContainer === getContainerBarcode(pkg) },
                  ]"
                  title="ກ໋ອບປີ້"
                  @click="copyContainerBarcode(getContainerBarcode(pkg))"
                ></i>
              </span>
              <span class="no-container" v-else>-</span>
            </td>
            <td class="col-chute">
              <span class="chute-badge" v-if="pkg.chute_number">
                {{ String(pkg.chute_number).padStart(2, "0") }}
              </span>
              <span class="no-chute" v-else>-</span>
            </td>
            <td class="col-error">
              <span class="error-text" v-if="pkg.error_reason">{{
                pkg.error_reason
              }}</span>
              <span class="no-error" v-else>-</span>
            </td>
            <td class="col-date">
              <span class="date-text">{{ formatDate(pkg.created_at) }}</span>
            </td>
            <td class="col-date">
              <span class="date-text">{{ formatDate(pkg.updated_at) }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div class="empty-state" v-else>
        <i class="fal fa-box-open"></i>
        <p>ບໍ່ມີພັດສະດຸຄັດແຍກແລ້ວ</p>
      </div>

      <!-- Pagination -->
      <div
        class="pagination-container"
        v-if="packages.length > 0 && totalPages > 1"
      >
        <vs-pagination
          v-model="currentPage"
          :length="totalPages"
          @input="onPageChange"
        />
      </div>
    </div>

    <!-- Barcode Dialog -->
    <vs-dialog v-model="showBarcodeDialog" width="400px">
      <div class="barcode-dialog">
        <h4 class="dialog-title">
          <i class="fal fa-barcode"></i>
          ສະແກນພັດສະດຸ
        </h4>
        <div class="barcode-container">
          <barcode
            :value="selectedParcelNumber"
            tag="svg"
            :options="{
              displayValue: true,
              width: 2,
              height: 80,
              fontSize: 16,
              background: 'white',
              margin: 10,
            }"
          />
        </div>
        <div class="dialog-actions">
          <vs-button flat @click="showBarcodeDialog = false">ປິດ</vs-button>
        </div>
      </div>
    </vs-dialog>
  </div>
</template>

<script>
import Loading from "@/views/Loading";
import { ModelListSelect } from "vue-search-select";
import DatePicker from "vue2-datepicker";

export default {
  name: "SortedPackages",

  components: {
    Loading,
    ModelListSelect,
    DatePicker,
  },

  data() {
    return {
      machineCode: null,
      packages: [],
      isLoading: false,
      error: null,
      selectedDate: new Date().toLocaleDateString('en-CA'),
      statusFilter: "sorted,phase_1_completed,phase_2_completed,route_completed,packed",
      statusOptions: [
        { value: "sorted,phase_1_completed,phase_2_completed,route_completed,packed", label: "ທັງໝົດ" },
        { value: "sorted,phase_1_completed,phase_2_completed,route_completed", label: "ຄັດແຍກແລ້ວ" },
        { value: "packed", label: "ແພັກແລ້ວ" },
      ],
      totalCount: 0,
      sortedCount: 0,
      packedCount: 0,
      currentPage: 1,
      perPage: 50,
      totalPages: 1,
      autoRefreshInterval: 0,
      autoRefreshTimer: null,
      showBarcodeDialog: false,
      selectedParcelNumber: "",
      copiedParcel: null,
      copiedContainer: null,
      searchQuery: "",
    };
  },

  created() {
    this.machineCode = this.$route.query.machineCode;
    // Set status filter from query param if provided
    if (this.$route.query.status) {
      this.statusFilter = this.$route.query.status;
    }
    if (this.machineCode) {
      this.fetchPackages();
    }
  },

  methods: {
    async fetchPackages() {
      if (!this.machineCode) return;

      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.$http.get(
          `sorting/dashboard/machines/${this.machineCode}/packages`,
          {
            params: {
              status: this.statusFilter,
              page: this.currentPage,
              per_page: this.perPage,
              search: this.searchQuery || undefined,
              date: this.selectedDate || undefined,
            },
          }
        );

        if (response.data && response.data.success) {
          this.packages = response.data.data || [];
          // Handle status_counts
          if (response.data.status_counts) {
            // Sum up sorted statuses
            this.sortedCount =
              (response.data.status_counts.sorted || 0) +
              (response.data.status_counts.phase_1_completed || 0) +
              (response.data.status_counts.phase_2_completed || 0) +
              (response.data.status_counts.route_completed || 0);
            this.packedCount = response.data.status_counts.packed || 0;
          }
          // Handle pagination
          if (response.data.pagination) {
            this.totalCount = response.data.pagination.total || 0;
            this.totalPages = response.data.pagination.last_page || 1;
            this.currentPage = response.data.pagination.current_page || 1;
          } else {
            // Fallback for old format
            this.totalCount = response.data.count || this.packages.length;
            this.totalPages = Math.ceil(this.totalCount / this.perPage);
          }
        } else {
          this.error = response.data?.message || "ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ";
        }
      } catch (err) {
        console.error("Fetch packages error:", err);
        this.error = "ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ";
      } finally {
        this.isLoading = false;
      }
    },

    getStatusLabel(status) {
      const labels = {
        pending: "ລໍຖ້າດໍາເນີນການ",
        phase_1_completed: "ຄັດແຍກແລ້ວ Phase 1",
        sorting: "ລໍຖ້າຢືນຢັນການຄັດແຍກ",
        phase_2_completed: "ຄັດແຍກແລ້ວ Phase 2",
        sorted: "ຄັດແຍກແລ້ວ",
        route_completed: "ຄັດແຍກແລ້ວ Routed",
        packed: "ແພັກແລ້ວ",
        error: "ມີຂໍ້ຜິດພາດ",
      };
      return labels[status] || status;
    },

    getStatusClass(status) {
      const classes = {
        pending: "pending",
        phase_1_completed: "sorted",
        sorting: "sorting",
        phase_2_completed: "sorted",
        sorted: "sorted",
        route_completed: "sorted",
        packed: "packed",
        error: "error",
      };
      return classes[status] || "default";
    },

    getContainerBarcode(pkg) {
      // Return label_barcode if available, otherwise container_barcode
      return pkg.label_barcode || pkg.container_barcode || null;
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchPackages();
    },

    onStatusFilterChange() {
      this.currentPage = 1;
      // Update URL query to match status filter
      this.updateUrlQuery();
      this.fetchPackages();
    },

    onDateFilterChange() {
      this.currentPage = 1;
      this.fetchPackages();
    },

    updateUrlQuery() {
      const query = { ...this.$route.query };
      if (this.statusFilter) {
        query.status = this.statusFilter;
      } else {
        delete query.status;
      }
      // Only update if query changed
      if (query.status !== this.$route.query.status) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    onSearch() {
      this.currentPage = 1;
      this.fetchPackages();
    },

    clearSearch() {
      this.searchQuery = "";
      this.currentPage = 1;
      this.fetchPackages();
    },

    setAutoRefresh(interval) {
      // Clear existing timer
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }

      this.autoRefreshInterval = interval;

      // Set new timer if interval > 0
      if (interval > 0) {
        this.autoRefreshTimer = setInterval(() => {
          this.fetchPackages();
        }, interval);
      }
    },

    clearAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
      this.autoRefreshInterval = 0;
    },

    showBarcode(parcelNumber) {
      this.selectedParcelNumber = parcelNumber;
      this.showBarcodeDialog = true;
    },

    async copyParcelNumber(parcelNumber) {
      try {
        await navigator.clipboard.writeText(parcelNumber);
        this.copiedParcel = parcelNumber;
        setTimeout(() => {
          this.copiedParcel = null;
        }, 2000);
      } catch (err) {
        // Fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = parcelNumber;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        this.copiedParcel = parcelNumber;
        setTimeout(() => {
          this.copiedParcel = null;
        }, 2000);
      }
    },

    async copyContainerBarcode(barcode) {
      try {
        await navigator.clipboard.writeText(barcode);
        this.copiedContainer = barcode;
        setTimeout(() => {
          this.copiedContainer = null;
        }, 2000);
      } catch (err) {
        // Fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = barcode;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        this.copiedContainer = barcode;
        setTimeout(() => {
          this.copiedContainer = null;
        }, 2000);
      }
    },
  },

  watch: {
    "$route.query.machineCode": function (newVal) {
      if (newVal !== this.machineCode) {
        this.machineCode = newVal;
        this.currentPage = 1;
        this.fetchPackages();
      }
    },
    "$route.query.status": function (newVal) {
      if (newVal && newVal !== this.statusFilter) {
        this.statusFilter = newVal;
        this.currentPage = 1;
        this.fetchPackages();
      }
    },
  },

  beforeDestroy() {
    this.clearAutoRefresh();
  },
};
</script>

<style lang="scss" scoped>
.sorted-packages-page {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 992px) {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
  }

  .page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: #333;

    i {
      margin-right: 10px;
      color: #2e7d32;
    }

    @media (max-width: 576px) {
      font-size: 18px;
    }
  }

  .machine-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #e3f2fd;
    color: #1565c0;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;

    i {
      font-size: 12px;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    @media (max-width: 992px) {
      justify-content: flex-start;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;

      > div {
        width: 100%;
      }
    }
  }
}

.stats-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;

  .stats-summary {
    display: flex;
    gap: 15px;
    margin-bottom: 0;
    padding: 0;
    background: transparent;
    border-radius: 0;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;

  .search-input-wrapper {
    position: relative;

    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 14px;
    }

    .search-input {
      width: 250px;
      padding: 9px 35px 9px 38px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      font-size: 13px;
      outline: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;

      &::placeholder {
        color: #aaa;
      }

      &:hover {
        border-color: #ccc;
      }

      &:focus {
        border-color: #2e7d32;
        box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.1);
      }
    }

    .clear-icon {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 14px;
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        color: #2e7d32;
        background: #e8f5e9;
      }
    }
  }
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }

  .filter-label {
    font-size: 12px;
    color: #666;
    white-space: nowrap;
    min-width: 40px;
  }

  .date-picker-input {
    width: 140px;

    @media (max-width: 768px) {
      flex: 1;
      width: auto;
    }

    ::v-deep .mx-input {
      height: 36px;
      font-size: 13px;
      border-radius: 8px;
      border: 1px solid #e0e0e0;
      transition: border-color 0.2s ease;
      font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;

      &:hover,
      &:focus {
        border-color: #2e7d32;
      }
    }

    ::v-deep .mx-icon-calendar {
      color: #2e7d32;
    }
  }
}

.status-filter {
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }

  .filter-label {
    font-size: 12px;
    color: #666;
    white-space: nowrap;
    min-width: 50px;
  }

  .status-select {
    min-width: 180px;

    @media (max-width: 768px) {
      flex: 1;
      min-width: auto;
    }
  }

  ::v-deep .ui.search.dropdown {
    min-height: 36px !important;
    min-width: 180px !important;
    font-size: 13px !important;
    border-radius: 8px !important;
    border: 1px solid #e0e0e0 !important;
    transition: border-color 0.2s ease;

    @media (max-width: 768px) {
      min-width: 0 !important;
      width: 100% !important;
    }

    &:hover,
    &:focus,
    &.active {
      border-color: #2e7d32 !important;
    }

    > .text {
      font-size: 13px !important;
    }
  }
}

.auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }

  .refresh-label {
    font-size: 12px;
    color: #666;
    white-space: nowrap;
  }

  .refresh-options {
    display: flex;
    gap: 4px;

    .refresh-btn {
      padding: 4px 10px;
      border: 1px solid #ddd;
      background: white;
      border-radius: 4px;
      font-size: 12px;
      color: #666;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;

      &:hover {
        border-color: #2e7d32;
        color: #2e7d32;
      }

      &.active {
        background: #2e7d32;
        border-color: #2e7d32;
        color: white;
      }
    }
  }

  .refresh-indicator {
    color: #2e7d32;
    font-size: 14px;
  }
}

.stats-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;

  .summary-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 15px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e0e0e0;

    .summary-label {
      font-size: 13px;
      color: #666;
    }

    .summary-value {
      font-size: 16px;
      font-weight: 700;
      color: #333;
    }

    &.sorted {
      border-left: 3px solid #2e7d32;
      .summary-value {
        color: #2e7d32;
      }
    }

    &.packed {
      border-left: 3px solid #7b1fa2;
      .summary-value {
        color: #7b1fa2;
      }
    }
  }
}

.loading-container,
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.error-content {
  text-align: center;
  color: #666;

  i {
    font-size: 48px;
    color: #2e7d32;
    margin-bottom: 15px;
  }

  p {
    font-size: 16px;
    margin-bottom: 15px;
  }
}

.table-container {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.packages-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8f9fa;

    th {
      padding: 14px 12px;
      text-align: left;
      font-weight: 600;
      font-size: 13px;
      color: #333;
      border-bottom: 2px solid #e0e0e0;
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.2s;

      &:hover {
        background: #fafafa;
      }

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 12px;
      font-size: 13px;
      color: #333;
    }
  }

  .col-no {
    width: 60px;
    text-align: center;
    color: #999;
  }

  .col-shipment,
  .col-parcel {
    .shipment-number,
    .parcel-number {
      font-family: monospace;
      font-weight: 600;
      color: #1565c0;
    }

    .parcel-actions {
      display: inline-flex;
      align-items: center;
      gap: 8px;

      .action-icon {
        font-size: 13px;
        color: #90a4ae;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
          color: #1565c0;
          background: #e3f2fd;
        }

        &.copied {
          color: #4caf50;
        }
      }
    }
  }

  .col-status {
    .status-badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;

      &.pending {
        background: #fff3e0;
        color: #f57c00;
      }

      &.sorting {
        background: #e3f2fd;
        color: #1565c0;
      }

      &.sorted {
        background: #e8f5e9;
        color: #2e7d32;
      }

      &.packed {
        background: #f3e5f5;
        color: #7b1fa2;
      }

      &.error {
        background: #ffebee;
        color: #c62828;
      }
    }
  }

  .col-container {
    .container-barcode {
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .barcode-text {
        font-family: monospace;
        font-weight: 600;
        color: #7b1fa2;
        background: #f3e5f5;
        padding: 3px 8px;
        border-radius: 4px;
        font-size: 12px;
      }

      .action-icon {
        font-size: 12px;
        color: #90a4ae;
        cursor: pointer;
        padding: 3px;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
          color: #7b1fa2;
          background: #f3e5f5;
        }

        &.copied {
          color: #4caf50;
        }
      }
    }

    .no-container {
      color: #999;
    }
  }

  .col-chute {
    .chute-badge {
      display: inline-block;
      padding: 4px 10px;
      background: #e8f5e9;
      color: #2e7d32;
      border-radius: 8px;
      font-weight: 600;
      font-family: monospace;
    }

    .no-chute {
      color: #999;
    }
  }

  .col-error {
    .error-text {
      color: #c62828;
      font-size: 12px;
    }

    .no-error {
      color: #999;
    }
  }

  .col-date {
    .date-text {
      color: #666;
      font-size: 12px;
    }
  }
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #999;

  i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #ddd;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 20px;
  border-top: 1px solid #f0f0f0;
}

// Barcode Dialog Styles
.barcode-dialog {
  padding: 10px;

  .dialog-title {
    font-weight: bold;
    font-size: 18px;
    margin-bottom: 20px;
    color: #333;

    i {
      margin-right: 8px;
      color: #1565c0;
    }
  }

  .barcode-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background: #fafafa;
    border-radius: 10px;
    border: 1px solid #e0e0e0;
    margin-bottom: 20px;

    svg {
      max-width: 100%;
    }
  }

  .dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 15px;
    border-top: 1px solid #eee;
  }
}
</style>
